import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useI18n } from "@bookingcom/lingojs-react";
import { goal } from "@flights/et-universal";
import { flightsLocalStore } from "@flights/web-api-utils-universal";
import { clientFetch } from "@flights/client-fetch";
import { trackMetaLandingsCustomGoalsV2 } from "../../../utils/experiments/track-meta-landings-v2.js";
import { CURRENT_ORDER_ID_KEY, PRE_OPTED_ANCILLARIES_QUERY_PARAMS } from "../../../constants.js";
import useGetUrlParams from "../../../hooks/useGetUrlParams.js";
import { getPreOptedAncillaries } from "../../../ancillaries/store/extraProducts/selectors.js";
import { preOptedAncillariesURLParams } from "../../../utils/preOptedAncillaries.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { getFlightDetailsFetchStatus } from "../../../store/flightDetails/selectors.js";
import useClientMetrics from "../../../hooks/useClientMetrics.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import useRetryFlightDetailsApi from "../../../hooks/useRetryFlightDetailsApi.js";
import useProfiler from "../../../hooks/useProfiler.js";
import { getSearchType } from "../../../store/search/selectors.js";
import { getModalTitle } from "./components/flightDetailsUtils.js";
import { useTrackBaggageInPaxMobile } from "../../../utils/experiments/funnel/flights_funnel_baggage_in_pax_mobile_2.js";
import useIsBrandedFare from "../../../branded-fares/hooks/useIsBrandedFare.js";
import { getIsFlexibleTicketPreSelectedAtMeta } from "../../../store/selectors/checkoutSteps.js";
export function useFlightDetailsInnerMobileController(props) {
    const { flight, flightToken, alternativeFaresFlow, hasPrefetchedBrandedFareOffers } = props;
    const i18n = useI18n();
    const history = useHistory();
    const { createUrl } = useCreateUrl();
    const { requestId } = useGlobalContext();
    const { getUrlParam } = useGetUrlParams();
    const { isLoggedInAuthLow } = useProfiler();
    const searchType = useSelector(getSearchType);
    const { trackClientMetric } = useClientMetrics();
    const fetchStatus = useSelector(getFlightDetailsFetchStatus);
    const trackV2 = useEventTracking("flight_details", requestId);
    const preOptedAncillaries = useSelector(getPreOptedAncillaries);
    const { isRetryableFlightDetails } = useRetryFlightDetailsApi();
    const isBrandedFare = useIsBrandedFare();
    const isFlexibleTicketPreSelectedAtMeta = useSelector(getIsFlexibleTicketPreSelectedAtMeta);
    const trackBaggageInPaxMobile = useTrackBaggageInPaxMobile({
        hasBrandedFarePage: isBrandedFare && !isFlexibleTicketPreSelectedAtMeta
    });
    const goToCheckout = useCallback((flightToken) => {
        let preOpted = preOptedAncillaries;
        if (getUrlParam(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket)) {
            preOpted = { ...preOpted, flexibleTicket: true };
        }
        const urlParams = preOptedAncillariesURLParams(preOpted);
        const baseOfferToken = getUrlParam("baseOfferToken");
        if (baseOfferToken)
            urlParams.set("baseOfferToken", baseOfferToken);
        if (hasPrefetchedBrandedFareOffers)
            urlParams.set("hasPrefetchedBrandedFareOffers", "true");
        const url = createUrl(`/checkout/start/${flightToken}?${urlParams}`);
        history.push(url);
    }, [preOptedAncillaries, history, createUrl, getUrlParam, hasPrefetchedBrandedFareOffers]);
    const onFlightSelect = useCallback(() => {
        if (flight) {
            const orderId = flightsLocalStore.get(CURRENT_ORDER_ID_KEY);
            if (alternativeFaresFlow) {
                void clientFetch("/api/order/track-alternative-fares-event", {
                    method: "POST",
                    body: JSON.stringify({
                        eventType: "ALTERNATIVE_FARE_SELECTED",
                        orderId,
                        selectedOffer: flight
                    }),
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
            }
            trackMetaLandingsCustomGoalsV2(3, "mdot");
            if (fetchStatus !== "success")
                trackClientMetric("flight_details_quick_checkout_start");
            trackV2("select_flight_details_next");
            goal("flights_flight_details_action_click");
            trackBaggageInPaxMobile();
            goToCheckout(flightToken);
        }
    }, [
        flight,
        alternativeFaresFlow,
        fetchStatus,
        trackClientMetric,
        trackV2,
        trackBaggageInPaxMobile,
        goToCheckout,
        flightToken
    ]);
    const isError = useMemo(() => {
        return fetchStatus === "failed" && !(flight && isRetryableFlightDetails());
    }, [flight, fetchStatus, isRetryableFlightDetails]);
    const shouldShowWishlistWidget = useMemo(() => {
        return isLoggedInAuthLow();
    }, [isLoggedInAuthLow]);
    const modalTitle = useMemo(() => {
        return getModalTitle(i18n, flight?.segments, searchType, true);
    }, [flight, i18n, searchType]);
    const [isFocusedOnClose, setIsFocusedOnClose] = useState(false);
    const closeBtnRef = useCallback((node) => {
        if (node && !isFocusedOnClose) {
            node.focus();
            setIsFocusedOnClose(true);
        }
    }, [isFocusedOnClose]);
    const campaignBadges = useMemo(() => {
        return fetchStatus === "success" ? flight?.campaignDisplay?.badges ?? [] : [];
    }, [fetchStatus, flight?.campaignDisplay?.badges]);
    return {
        isError,
        trackV2,
        modalTitle,
        closeBtnRef,
        campaignBadges,
        onFlightSelect,
        shouldShowWishlistWidget
    };
}
